import { motion } from "framer-motion";
import { Cpu, Cog, KeyRound, Copy, Wrench, ArrowRight, Activity, CircuitBoard } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { prefillInquiry } from "./smooth";
import { WhatsAppIcon } from "./WhatsApp";
import { WHATSAPP_B2B_URL } from "@/lib/api";
import { useT } from "@/lib/i18n";

const ICONS = [Cpu, Copy, Cog, KeyRound, Activity, CircuitBoard];
const ANCHORS = [undefined, undefined, undefined, undefined, "bildiagnose", undefined];
const TXT = {
  no: {
    units: [
      ["ECU / TCU", "ECU / TCU programmering", "Programmering og koding av motor- og girkassestyring etter bytte eller oppdatering."],
      ["Kloning", "Kloning av styreenheter", "Data overføres fra gammel til ny eller brukt styreenhet, slik at den passer bilen."],
      ["Motor & gir", "Motorstyring og girkassestyring", "Feilsøking, tilpasning og koding av motorstyring og girkassestyring."],
      ["IMMO", "Startsperre og immobilizer", "Tilpasning av startsperre etter bytte av styreenhet, slik at bilen starter med eksisterende nøkler."],
      ["Feilsøking", "Avansert elektronisk feilsøking", "Bildiagnostikk og systematisk feilsøking på bilelektronikk når vanlig diagnose ikke strekker til."],
      ["Reparasjon", "Diagnose og reparasjon av styreenheter", "Diagnose og reparasjon av elektroniske styreenheter."],
    ],
    steps: [
      ["Kontakt oss", "Send bilinfo og delenummer på WhatsApp eller via skjemaet."],
      ["Lever enheten", "Kom innom verkstedet med gammel og ny styreenhet."],
      ["Vi kloner", "Data overføres fra gammel til ny enhet og tilpasses bilen."],
      ["Klar til montering", "Du får enheten tilbake, klar til å monteres."],
    ],
    eyebrow: "ECU / TCU · Bilelektronikk", h2: ["ECU- og TCU-", "programmering", "& kloning."],
    p1: "Vi tilbyr ECU-programmering i Stavanger, TCU-programmering, ECU-kloning og reparasjon av styreenheter, i tillegg til bilelektronikk og bildiagnostikk.",
    p2: "RB TUN AS tilbyr også spesialiserte tjenester til andre verksteder og bilforhandlere i Stavanger, Sandnes og Rogaland.",
    wa: "Send delenummer", form: "Send forespørsel", how: "Slik fungerer det", b2b: "Verksteder er velkomne – ta kontakt for samarbeid.",
  },
  en: {
    units: [
      ["ECU / TCU", "ECU / TCU programming", "Programming and coding of engine and gearbox control units after replacement or update."],
      ["Cloning", "Control unit cloning", "Data is transferred from the old unit to a new or used control unit, so it fits your car."],
      ["Engine & gearbox", "Engine and gearbox control", "Troubleshooting, adaptation and coding of engine and gearbox control units."],
      ["IMMO", "Immobiliser", "Immobiliser adaptation after a control unit replacement, so the car starts with your existing keys."],
      ["Diagnostics", "Advanced electronic troubleshooting", "Vehicle diagnostics and systematic troubleshooting of car electronics when standard diagnostics are not enough."],
      ["Repair", "Control unit diagnostics and repair", "Diagnostics and repair of electronic control units."],
    ],
    steps: [
      ["Contact us", "Send car details and part number on WhatsApp or via the form."],
      ["Drop off the unit", "Bring the old and the new control unit to our workshop."],
      ["We clone", "Data is transferred from the old unit to the new one and adapted to the car."],
      ["Ready to fit", "You get the unit back, ready to be installed."],
    ],
    eyebrow: "ECU / TCU · Car electronics", h2: ["ECU and TCU", "programming", "& cloning."],
    p1: "We offer ECU programming in Stavanger, TCU programming, ECU cloning and control unit repair, as well as car electronics and vehicle diagnostics.",
    p2: "RB TUN AS also provides specialised services for other workshops and car dealers in Stavanger, Sandnes and Rogaland.",
    wa: "Send part number", form: "Send inquiry", how: "How it works", b2b: "Workshops are welcome – get in touch about working together.",
  },
};

export const EcuSection = () => {
  const t = useT(TXT);
  const UNITS = t.units.map(([title, s, d], i) => ({ icon: ICONS[i], anchor: ANCHORS[i], t: title, s, d }));
  const STEPS = t.steps.map(([title, d], i) => ({ n: `0${i + 1}`, t: title, d }));
  return (
  <section id="ecu" data-testid="ecu-section" className="relative overflow-hidden">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-36">
      <div className="grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-6">
          <Eyebrow index="03">{t.eyebrow}</Eyebrow>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">
            {t.h2[0]}<br />{t.h2[1]}<br /><span className="text-[var(--red)]">{t.h2[2]}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 lg:self-end">
          <p className="text-base leading-relaxed text-zinc-300 md:text-lg">
            {t.p1}
            {" "}{t.p2}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href={WHATSAPP_B2B_URL} target="_blank" rel="noopener noreferrer" data-testid="ecu-whatsapp-button"
              className="flex items-center justify-center gap-3 bg-[#25D366] px-6 py-4 font-mono text-sm font-bold uppercase tracking-widest text-[#08090b] transition-transform hover:-translate-y-0.5">
              <WhatsAppIcon size={18} /> {t.wa}
            </a>
            <button onClick={() => prefillInquiry({ service: "kloning" })} data-testid="ecu-form-button"
              className="group flex items-center justify-center gap-3 border border-white/15 px-6 py-4 font-mono text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-white/50">
              {t.form} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>

      <div className="mt-16 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {UNITS.map((u, i) => (
          <Reveal key={i} delay={i * 0.06} className="h-full">
            <motion.article id={u.anchor} whileHover={{ backgroundColor: "rgba(255,42,42,0.08)" }} data-testid={`ecu-unit-${i}`}
              className="flex h-full flex-col bg-[var(--bg)] p-6 md:p-8">
              <u.icon size={26} className="text-[var(--red)]" />
              <div className="mt-8 font-display text-4xl font-black uppercase leading-none md:text-5xl">{u.t}</div>
              <h3 className="mt-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300">{u.s}</h3>
              <p className="mt-4 text-sm leading-relaxed text-zinc-300">{u.d}</p>
            </motion.article>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 grid gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <Copy size={20} className="text-[var(--red)]" />
            <h3 className="font-display text-3xl font-black uppercase leading-none md:text-4xl">{t.how}</h3>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            <Wrench size={14} className="mr-2 inline text-zinc-400" />{t.b2b}
          </p>
        </Reveal>
        <ol className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06}>
              <li className="h-full border-t-2 border-[var(--red)] bg-[var(--surface)] p-5" data-testid={`ecu-step-${s.n}`}>
                <span className="font-mono text-xs text-[var(--red)]">{s.n}</span>
                <div className="mt-2 font-display text-2xl font-black uppercase leading-none">{s.t}</div>
                <p className="mt-2 text-sm text-zinc-300">{s.d}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </div>
  </section>
  );
};
